// The flow's end screens, one per ?screen= (mock only).
//
// Test A's success screens and the paid-but-not-sent states (docs/30, #20) have
// no server behind them yet: the payment that sends a submission is still to be
// built. This page renders the real outcome component with each state's data,
// so the copy and layout can be reviewed before that work starts. Without
// ?screen= it lists them.
//
// It refuses to render outside the mock API.

import React, { useEffect } from "react";
import styled from "styled-components";
import qs from "query-string";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionOutcome, {
  OutcomeKind,
  OutcomePlan,
  OutcomeProOffer,
} from "../common/SubmissionOutcome";
import { isMockApi } from "../../../shared/api/submission";

interface Props {
  history: any;
  location: any;
}

interface IOutcomeScreen {
  id: string;
  label: string;
  kind: OutcomeKind;
  plan?: OutcomePlan;
  withTrialEnd?: boolean;
  proOffer?: OutcomeProOffer;
}

const BREAKDOWN_NAME = "Streaming Series - Northern Line";
const TRIAL_DAYS = 30;

export const OUTCOME_SCREENS: IOutcomeScreen[] = [
  { id: "a-trial-sent", label: "Test A: trial started, submission sent",
    kind: "sent", plan: "trial", withTrialEnd: true },
  { id: "a-trial-sent-no-date", label: "Test A: trial started (no end date), submission sent",
    kind: "sent", plan: "trial" },
  { id: "a-pro-sent", label: "Test A: PRO started, submission sent",
    kind: "sent", plan: "pro" },
  { id: "a-send-failed", label: "Test A: trial started, send failed",
    kind: "sendFailed", plan: "trial", withTrialEnd: true },
  { id: "closed-after-trial", label: "Trial started, casting call closed",
    kind: "closedAfterPayment", plan: "trial", withTrialEnd: true },
  { id: "closed-after-pro", label: "PRO started, casting call closed",
    kind: "closedAfterPayment", plan: "pro" },
  { id: "country-after-trial", label: "Trial started, casting doesn't take their country",
    kind: "countryAfterPayment", plan: "trial", withTrialEnd: true },
  { id: "sent", label: "Sent, nothing owed (test B, PRO, active trial)",
    kind: "sent" },
  { id: "us-sent", label: "US actor: sent, PRO offer",
    kind: "sent", proOffer: "us" },
  { id: "free-call-sent", label: "Free casting call outside the US: sent, PRO offer",
    kind: "sent", proOffer: "freeBreakdown" },
];

const MockOutcomesPage: React.FC<Props> = ({ history, location }) => {
  useEffect(() => {
    if (!isMockApi()) history.replace("/submission/casting-calls");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!isMockApi()) return null;

  const id = (qs.parse(location.search).screen as string) || "";
  const screen = OUTCOME_SCREENS.find((sc) => sc.id === id);
  const noop = (): void => undefined;

  if (!screen) {
    return (
      <SubmissionLayout>
        <List>
          {OUTCOME_SCREENS.map((sc) => (
            <li key={sc.id}>
              <a href={`/submission/mock-outcomes?screen=${sc.id}`}>{sc.label}</a>
            </li>
          ))}
        </List>
      </SubmissionLayout>
    );
  }

  const trialEndsUtc = screen.withTrialEnd
    ? new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString()
    : null;

  return (
    <SubmissionLayout>
      <SubmissionOutcome
        kind={screen.kind}
        plan={screen.plan || null}
        trialEndsUtc={trialEndsUtc}
        proOffer={screen.proOffer || null}
        breakdownName={BREAKDOWN_NAME}
        onDashboard={noop}
        onExplore={noop}
        onSeeCastingCalls={noop}
        onGoPro={noop}
        onRetry={noop}
      />
    </SubmissionLayout>
  );
};

const List = styled.ul`
  margin: 24px 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 28px;

  a {
    color: #0f9bf0;
  }
`;

export default MockOutcomesPage;
